/**
 * The shapes `vault-api` returns, and the two rules every screen reads them by.
 *
 * These lived as private `type Doc = {...}` blocks in three components, which had
 * already drifted: `Docs.svelte` declared a `notes` field the API never sent, so
 * the edit form round-tripped an empty string and quietly erased it. One
 * declaration, imported everywhere, is what stops that class of bug.
 */

/**
 * A deterministic cross-check that failed — **never a confidence percentage**.
 *
 * `field` names the fact it failed on, so the object page puts the mark beside the
 * amount rather than in a footnote. A score would invite arguing with a number
 * nobody can interpret; a sentence can be agreed with or corrected.
 */
export type Check = { field: string | null; says: string };

export type Doc = {
  id: string;
  title: string;
  docType: string;
  supplier: string | null;
  policyNumber: string | null;
  amount: number | null;
  documentDate: string | null;
  expiresAt: string | null;
  expiryKind: string | null;
  inCase: boolean;
  hasFile: boolean;
  checks: Check[];
  replacedBy: string | null;
  createdAt: string | null;
  notes: string | null;
  notifyOnExpiry: boolean;
};

export type DocDetail = Doc & {
  mime: string | null;
  sizeBytes: number | null;
  replaces: string | null;
  replacesTitle: string | null;
  replacedByTitle: string | null;
  estate: {
    originalLocation: string | null;
    contact: { name: string; role: string; phone: string | null } | null;
    reference: string | null;
    urgency: "first" | "later";
    notes: string | null;
  } | null;
  createdBy: string | null;
  updatedAt: string | null;
  taxYear: string | null;
};

export type ExpiringRow = {
  id: string;
  title: string;
  expiresAt: string;
  daysLeft: number;
  kindLabel: string;
  docType: string;
  overdue: boolean;
};

export type Meta = { docTypes: string[]; expiryKinds: string[]; suggested: Record<string, string> };

export type TrailEntry = {
  at: string;
  kind: "vault.doc.open" | "vault.incase.open" | "vault.doc.filed" | "vault.doc.edited";
  by: string;
  fields: string[];
  inCase: boolean;
};

export type Citation = {
  id: string;
  title: string;
  docType: string;
  supplier: string | null;
  documentDate: string | null;
  expiresAt: string | null;
  hasFile: boolean;
  inCase: boolean;
  field: string | null;
};

/** F17. `answer` is non-null only when `cite` is — the receipt rule, in the type. */
export type Answer = {
  question: string;
  answer: string | null;
  because: string | null;
  cite: Citation | null;
  alsoSeen: { id: string; title: string }[];
};

export type TaxYearGroup = {
  label: string;
  endYear: number;
  start: string;
  end: string;
  docs: Doc[];
};

// ---------------------------------------------------------------------------
// Expiry — the one tight column, decided once
// ---------------------------------------------------------------------------

/**
 * How a document's expiry reads. **Thirty days is the line** (`04-portal-screens.md`).
 *
 * Three states, and the two that carry weight carry a glyph and a word as well as
 * the colour — "12 days left" is legible with no colour vision at all, which is
 * the constraint this estate is built around.
 *
 * `null` for a document that does not expire. The caller renders an em dash, never
 * a zero and never a blank: a blank cell reads as missing data, and this is a fact.
 */
export type Expiry =
  | null
  | { tone: "warn"; glyph: "alert" | "clock"; word: string; days: number; at: string }
  | { tone: "quiet"; glyph: null; word: string; days: number; at: string };

/** Whole days from today to a date, in Africa/Johannesburg terms. SAST is UTC+2, no DST. */
function daysUntil(iso: string): number {
  const startOfLocalDay = (ms: number) => {
    const d = new Date(ms + 2 * 3_600_000);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - 2 * 3_600_000;
  };
  return Math.round((startOfLocalDay(Date.parse(iso)) - startOfLocalDay(Date.now())) / 86_400_000);
}

export const EXPIRY_SOON_DAYS = 30;

export function expiryOf(iso: string | null | undefined): Expiry {
  if (!iso) return null;
  const days = daysUntil(iso);
  if (days < 0) {
    return { tone: "warn", glyph: "alert", word: `expired ${Math.abs(days)} days ago`, days, at: iso };
  }
  if (days === 0) return { tone: "warn", glyph: "alert", word: "expires today", days, at: iso };
  if (days <= EXPIRY_SOON_DAYS) {
    // `clock` rather than `alert` above zero: nothing is wrong yet, something is
    // approaching, and using the same glyph for both would make the one that means
    // "act now" indistinguishable from the one that means "note it".
    return { tone: "warn", glyph: "clock", word: `${days} days left`, days, at: iso };
  }
  return { tone: "quiet", glyph: null, word: `${days} days left`, days, at: iso };
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

const WORDS = [
  "Nothing", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
  "Eight", "Nine", "Ten", "Eleven", "Twelve",
];

/**
 * Small counts as words, larger ones as numerals.
 *
 * The title sentence is prose a household reads — "Four waiting in intake", not
 * "4 waiting in intake" — and prose switches to numerals somewhere around a dozen
 * because "one hundred and forty-two documents" is worse than "142 documents".
 */
export function inWords(n: number): string {
  return n < WORDS.length ? WORDS[n]! : String(n);
}

/** Lower-cased, for mid-sentence use. */
export function inWordsLower(n: number): string {
  const w = inWords(n);
  return n < WORDS.length ? w.toLowerCase() : w;
}

/** A document type as words. "municipal_bill" → "Municipal bill". */
export function pretty(t: string): string {
  const s = t.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}
